import type { MaybeRef, MaybeRefOrGetter } from 'vue'
import type { ContentFormWriter } from '~/models/Content/ContentFormWriter'
import type { AssignmentAnswer, AssignmentAnswerData, AssignmentResponse } from '~/models/Assignment'
import { useI18n } from 'vue-i18n'
import { computed, toValue } from 'vue'
import { storeToRefs } from 'pinia'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { AuToast } from '~/stores/toasts'
import useAuthStore from '~/stores/auth'
import useMetadataApi from '~/api/metadataApi'
import useContentApi from '~/api/contentApi'
import useAssignmentApi from '~/api/assignmentApi'

export default function useFormWriter(
  locationId: MaybeRefOrGetter<number>,
  user?: MaybeRefOrGetter<string | undefined>,
  isAssignment: boolean = false,
  enabled: MaybeRef<boolean> = true,
) {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const { isTeacher, userId, isAuthenticated } = storeToRefs(useAuthStore())

  const { findContents } = useContentApi()
  const { get: getMetadata, set: setMetadata } = useMetadataApi()
  const { getStudentAssignmentResponse, saveStudentAssignment, createStudentAssignment } = useAssignmentApi()

  const assignmentId = computed(() => `form_writer-${toValue(locationId)}`)
  const metadataId = computed(() => `formWriter-${toValue(locationId)}`)

  const currentUserId = computed(() => toValue(user))

  const assignmentQueryKey = computed(() => ['form_writer_assignment', assignmentId.value, currentUserId.value])
  const metadataQueryKey = computed(() => ['form_writer_metadata', metadataId.value, currentUserId.value])

  const assignmentEnabled = computed(() => (isTeacher.value ? userId.value !== currentUserId.value : true)
    && !!toValue(locationId)
    && !!currentUserId.value
    && isAssignment
    && isAuthenticated.value
  )

  const metadataEnabled = computed(() => !!toValue(locationId)
    && isAssignment
    && isAuthenticated.value
  )

  const {
    data,
    isLoading: isLoadingContent,
    isError
  } = useQuery({
    queryKey: computed(() => ['form_writer', toValue(locationId)]),
    queryFn: async () => (await findContents<ContentFormWriter>({
      locationIdCriterion: [toValue(locationId)],
      mainLocationCriterion: true,
    }))[0],
    staleTime: Infinity,
    enabled: !!toValue(locationId) && toValue(enabled),
    retry: 1,
  })

  const {
    data: assignment,
    isLoading: isLoadingAssignment
  } = useQuery({
    queryKey: assignmentQueryKey,
    queryFn: () => getStudentAssignmentResponse(assignmentId.value, currentUserId.value as string),
    staleTime: Infinity,
    enabled: assignmentEnabled,
    retry: false,
  })

  const {
    data: metadata,
    isLoading: isLoadingMetadata
  } = useQuery({
    queryKey: metadataQueryKey,
    queryFn: () => getMetadata<AssignmentAnswerData>(metadataId.value),
    staleTime: Infinity,
    enabled: metadataEnabled,
    retry: false,
  })

  // This is structured so we only fetch a fresh assignment the first time we enter something
  const updateAssignment = useMutation({
    mutationFn: async (answer: AssignmentAnswer) =>
      saveStudentAssignment(assignmentId.value, currentUserId.value as string, answer),
    onSuccess: (data) => {
      // Check if we have existing data in the cache
      const existingData = queryClient.getQueryData(assignmentQueryKey.value) as AssignmentResponse | undefined

      // Invalidate if no data exists or if answer array is empty
      if (!existingData || !existingData.answer || existingData.answer.length === 0) {
        // If no existing data or empty answer array, invalidate the query to fetch fresh data
        void queryClient.invalidateQueries({ queryKey: assignmentQueryKey.value })
      } else {
        // If we have existing data with answers, update it in the cache without refetching
        queryClient.setQueryData(assignmentQueryKey.value, (oldData: AssignmentResponse) => ({
          ...oldData,
          answer: oldData.answer.map(item =>
            item.isDraft ? data : item
          )
        }))
      }
    }
  })

  const updateMetadata = useMutation({
    mutationFn: async (value: AssignmentAnswerData) =>
      setMetadata(metadataId.value, value, currentUserId.value as string),
    onSuccess: data => queryClient.setQueryData(metadataQueryKey.value, () => data),
  })

  const saveAssignment = useMutation({
    mutationFn: async (answer: AssignmentAnswer) =>
      createStudentAssignment(assignmentId.value, currentUserId.value as string, answer),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: assignmentQueryKey.value }),
    onSettled: (_, error) => error
      ? AuToast.error(t('formWriter.toasts.submitError'))
      : AuToast.success(t('formWriter.toasts.submitSuccess')),
  })

  const updateForm = (formData: AssignmentAnswerData) => {
    if (!isTeacher.value && isAssignment && assignment.value) {
      const draft = assignment.value?.answer.find(({ isDraft }) => isDraft)
      const draftAnswer = draft ? JSON.parse(draft?.data) : {}

      updateAssignment.mutateAsync({
        ...draftAnswer,
        isDraft: true,
        data: JSON.stringify(formData)
      })
    }

    updateMetadata.mutateAsync(formData)
  }

  return {
    data,
    assignment,
    metadata,

    isLoadingFormData: computed(() => isLoadingContent.value || isLoadingAssignment.value || isLoadingMetadata.value),
    isError,

    updateForm,
    saveAssignment,
  }
}
